import { MetricField } from '@/modules/optimizer/components/metrics/models/CommonMetricsModel';
import { useActiveTeamContext } from '@/modules/teams/contexts/ActiveTeamContext';
import { isNil } from 'lodash-es';
import { useMemo } from 'react';

type FormatterFunction = (value: number | null | undefined) => string;

const PERCENT_MAX = 999;

export enum CurrencyPosition {
  LEFT,
  RIGHT,
}

function useFormatting() {
  const { activeProfile } = useActiveTeamContext();
  const currencyCode = activeProfile && isValidCurrencyCode(activeProfile.currencyCode) ? activeProfile.currencyCode : 'USD';

  const percentageFormatter = useMemo(() => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  const formatNumbersAsAbbreviationsFormatter = (number: number | null | undefined): string => {
    if (isNil(number)) return '';
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
    }).format(number);
  };

  const formatPercent = (number: number | null | undefined): string => {
    if (isNil(number)) return '';

    const formatted = percentageFormatter.format(number);
    // 23.33% to 23.3%, but 0.25% stays as is
    if (number >= 0.1 || number <= -0.1) {
      return formatted.slice(0, -2) + formatted.slice(-1); // remove 1 digit with '%' and add trailing '%' back
    }

    return formatted;
  };

  const formatChangeInPercent = (current: number, previous: number): string => {
    if (current === previous) return '0.00%';

    if (previous === 0) return '-';

    const change = (current - previous) / Math.abs(previous);
    return formatPercent(change);
  };

  const formatChangeInUnsignedPercentWithArrow = (current: number, previous: number): string => {
    if (current === previous) return '0.00%';

    if (previous === 0) return '-';

    const change = current / previous - 1;
    const arrow = getDirectionArrow(change);
    if (change * 100 > PERCENT_MAX) {
      return `${PERCENT_MAX}+% ` + arrow;
    } else if (change * 100 < -1 * PERCENT_MAX) {
      return `-${PERCENT_MAX}% ` + arrow;
    }
    return formatPercent(change) + ' ' + arrow;
  };

  const formatPercentWithArrow = (number: number): string => {
    let changeDirection = 0;
    if (number > 0) {
      changeDirection = 1;
    } else if (number < 0) {
      changeDirection = -1;
    }
    const arrow = getDirectionArrow(changeDirection);
    if (number * 100 > PERCENT_MAX) {
      return `${PERCENT_MAX}+% ` + arrow;
    } else if (number * 100 < -1 * PERCENT_MAX) {
      return `-${PERCENT_MAX}% ` + arrow;
    }
    return formatPercent(number) + ' ' + arrow;
  };

  const formatWithThousandsSeparatorFormatter = (number: number | null | undefined): string => {
    if (isNil(number)) return '';
    return new Intl.NumberFormat('en-US').format(number);
  };

  const formatWithThousandsSeparator = (number: number | null | undefined): string => {
    if (isNil(number)) return '';
    return formatWithThousandsSeparatorFormatter(parseFloat(number.toFixed(2)));
  };

  const formatCurrency = (number: number | null | undefined): string => {
    if (isNil(number)) return '';

    return number.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode,
    });
  };

  const getDirectionArrow = (number: number): string => {
    if (number == 0) return '';
    return number > 0 ? '↑' : '↓';
  };

  const formatWholePercent = (number: number | null | undefined): string => {
    if (isNil(number)) return '';

    return (number * 100).toFixed(0) + '%';
  };

  const formatCurrencyWithAbbreviations = (number: number | null | undefined): string => {
    if (isNil(number)) return '';

    return number.toLocaleString('en-US', {
      style: 'currency',
      currency: currencyCode,
      notation: 'compact',
    });
  };

  const formatDateStringTimeNoSeconds = (dateString: string | null | undefined): string => {
    try {
      if (isNil(dateString) || isNullTimestamp(dateString)) return '';
      const dateObj = new Date(dateString);

      // Format the date object to local time without seconds or milliseconds
      const formattedDate = new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
      }).format(dateObj);

      return formattedDate;
    } catch (err) {
      return dateString ?? '';
    }
  };

  const formatDateStringTimeNoHours = (dateString: string | null | undefined): string => {
    try {
      if (isNil(dateString) || isNullTimestamp(dateString)) return '';
      const dateObj = new Date(dateString);

      const formattedDate = new Intl.DateTimeFormat(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }).format(dateObj);

      return formattedDate;
    } catch (err) {
      console.log('Error parsing date: ', err);
      return dateString ?? '';
    }
  };

  function getCurrencySymbol() {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    // Extracts and returns the currency symbol from formatted string
    const value = formatter.format(0);
    return value.replace(/\d/g, '').trim();
  }

  function getCurrencySymbolPosition(): CurrencyPosition {
    const formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode }).format(0);
    const zeroPosition = formatted.indexOf('0');

    if (zeroPosition === 0) {
      return CurrencyPosition.RIGHT;
    } else {
      return CurrencyPosition.LEFT;
    }
  }

  const getShortFormatterForMetricField = (field: MetricField): FormatterFunction => {
    switch (field) {
      case MetricField.IMPRESSIONS:
      case MetricField.CLICKS:
      case MetricField.ORDERS:
      case MetricField.ROAS:
        return formatNumbersAsAbbreviationsFormatter;

      case MetricField.CTR:
      case MetricField.CVR:
      case MetricField.ACOS:
        return formatPercent;

      case MetricField.CPC:
      case MetricField.SPEND:
      case MetricField.SALES:
      case MetricField.RPC:
      case MetricField.CPA:
      case MetricField.AOV:
      case MetricField.CPM:
        return formatCurrencyWithAbbreviations;

      default:
        return assertAllMetricFieldFormattersDescribed(field);
    }
  };

  const getLongFormatterForMetricField = (field: MetricField): FormatterFunction => {
    switch (field) {
      case MetricField.IMPRESSIONS:
      case MetricField.CLICKS:
      case MetricField.ORDERS:
      case MetricField.ROAS:
        return formatWithThousandsSeparator;

      case MetricField.CTR:
      case MetricField.CVR:
      case MetricField.ACOS:
        return formatPercent;

      case MetricField.CPC:
      case MetricField.SPEND:
      case MetricField.SALES:
      case MetricField.RPC:
      case MetricField.CPA:
      case MetricField.AOV:
      case MetricField.CPM:
        return formatCurrency;

      default:
        return assertAllMetricFieldFormattersDescribed(field);
    }
  };
  return {
    formatPercent,
    formatWholePercent,
    formatNumbersAsAbbreviations: formatNumbersAsAbbreviationsFormatter,
    formatWithThousandsSeparator,
    formatChangeInPercent,
    formatChangeInUnsignedPercentWithArrow,
    formatPercentWithArrow,
    getDirectionArrow,
    formatCurrency,
    formatCurrencyWithAbbreviations,
    getCurrencySymbolPosition,
    getCurrencySymbol,
    formatDateStringTimeNoSeconds,
    formatDateStringTimeNoHours,
    getShortFormatterForMetricField,
    getLongFormatterForMetricField,
  };
}

const assertAllMetricFieldFormattersDescribed = (x: never): never => {
  throw new Error(`Unhandled case: ${x}`);
};

function isValidCurrencyCode(code: string): boolean {
  try {
    new Intl.NumberFormat('en-US', { style: 'currency', currency: code }).format(0);
    return true;
  } catch (e) {
    console.error('Not a valid currency code: ', code);
    return false;
  }
}

export default useFormatting;

function isNullTimestamp(date: string | null | undefined): boolean {
  return date === '' || date === '0000-00-00 00:00:00' || date === '0001-01-01T00:53:28+00:53';
}
